import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, ArrowRight } from "lucide-react";

// Feb 2026 setup reminder banner.
//
// Small persistent nudge shown on main app pages when Account setup is
// below 100 percent. Reads /api/onboarding/checklist to compute
// progress. No new endpoint, no new backend field.
//
// Rules:
//   - Only rendered for workspace admins (role === "company_admin").
//   - Hidden on /app/setup (Account setup itself).
//   - Hidden on /app/overview because Overview already has its own
//     "Continue setup" card.
//   - Hidden when setup is 100 percent complete.
//   - "Hide for now" hides for that company on that browser via
//     localStorage. As soon as setup reaches 100 percent, the hidden
//     flag is cleared so the banner never reappears in a stale state.

const HIDDEN_PREFIX = "nextrota.setup.reminder_hidden.";

export default function SetupReminderBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [shiftTemplatesCount, setShiftTemplatesCount] = useState(null);
  const [hidden, setHidden] = useState(false);

  const isCompanyAdmin = user?.role === "company_admin";
  const companyId = user?.company_id || "";
  const storageKey = `${HIDDEN_PREFIX}${companyId}`;

  useEffect(() => {
    if (!isCompanyAdmin || !companyId) return;
    try { setHidden(localStorage.getItem(storageKey) === "1"); }
    catch { /* ignore */ }
  }, [isCompanyAdmin, companyId, storageKey]);

  useEffect(() => {
    if (!isCompanyAdmin) { setData(null); return; }
    let cancelled = false;
    Promise.all([
      api.get("/onboarding/checklist"),
      api.get("/shift-templates").catch(() => ({ data: [] })),
    ]).then(([cl, tpl]) => {
      if (cancelled) return;
      setData(cl.data);
      setShiftTemplatesCount((tpl.data || []).length);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
    // Re-fetch when the user navigates to a new route so progress stays fresh.
  }, [isCompanyAdmin, location.pathname]);

  if (!isCompanyAdmin || !data) return null;

  // Compute banner counts against the 10 visible Account setup steps
  // only. Backend percent covers 13 keys, which would otherwise
  // disagree with what the manager sees inside the wizard. Shift
  // templates step is derived client-side by counting rows on
  // /api/shift-templates so the count matches the wizard exactly.
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const ACCOUNT_SETUP_KEYS = new Set([
    "company_type", "categories", "skills", "bank_holidays",
    "locations", "team", "pattern", "employee", "first_roster",
  ]);
  const backendSteps = steps.filter((s) => ACCOUNT_SETUP_KEYS.has(s.key));
  const backendDone = backendSteps.filter((s) => s.done).length;
  const shiftTemplatesDone = (shiftTemplatesCount || 0) > 0;
  const total = 10;
  const done = Math.min(total, backendDone + (shiftTemplatesDone ? 1 : 0));
  const pct = Math.round((done / total) * 100);

  // Clear stale hidden flag once setup is done.
  if (pct >= 100) {
    if (hidden) {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    }
    return null;
  }
  if (hidden) return null;

  // Hide on setup itself and on Overview (Overview shows its own card).
  const path = location.pathname || "";
  if (path.startsWith("/app/setup")) return null;
  // The Overview page is rendered by the `/app` index route, so its
  // path is `/app` (not `/app/overview`). Hide the reminder banner on
  // both the index route and any explicit `/app/overview` route.
  if (path === "/app" || path === "/app/") return null;
  if (path === "/app/overview" || path === "/app/overview/") return null;

  const hide = () => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div
      data-testid="setup-reminder-banner"
      className="mb-4 rounded-xl border border-[#E4EAF4] bg-gradient-to-br from-[#F5F8FE] to-[#EFF3FB] px-4 py-3 flex items-center gap-3"
    >
      <div className="h-9 w-9 rounded-full bg-white border border-[#E4EAF4] flex items-center justify-center shrink-0">
        <Sparkles className="h-4 w-4 text-[#2563EB]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-[#0B1A36]">
          Finish setting up NextRota
        </p>
        <p className="text-[11px] text-[#4B5773] mt-0.5"
           data-testid="setup-reminder-progress">
          {done} of {total} steps complete
        </p>
        <div className="h-1 bg-[#E4EAF4] rounded-full mt-1.5 overflow-hidden max-w-[280px]">
          <div className="h-full bg-emerald-500 transition-[width] duration-500"
               style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link to="/app/setup"
              data-testid="setup-reminder-continue"
              className="text-[12px] font-medium text-white bg-[#2563EB] hover:bg-[#1D4ED8] px-3 py-1.5 rounded-md inline-flex items-center gap-1">
          Continue setup <ArrowRight className="h-3 w-3" />
        </Link>
        <button
          type="button"
          onClick={hide}
          title="Hide for now"
          data-testid="setup-reminder-hide"
          className="h-7 w-7 grid place-items-center rounded-md text-[#4B5773] hover:bg-white/70"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
